/**
 * Template Categories Zustand Store
 *
 * Manages template categories for Super Admin:
 * - List categories by type (form, document)
 * - CRUD operations
 * - Reordering
 */
import { create } from 'zustand';
import { getApiClient } from '@respectly/api-client';
import type {
  TemplateCategoryEntity,
  CreateTemplateCategoryDto,
  UpdateTemplateCategoryDto,
  TemplateCategoryType,
} from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface CategoriesError {
  code: string;
  message: string;
}

// ============================================
// State Interface
// ============================================

export interface CategoriesState {
  // Form Categories
  formCategories: TemplateCategoryEntity[];
  isLoadingFormCategories: boolean;
  formCategoriesError: CategoriesError | null;

  // Document Categories
  documentCategories: TemplateCategoryEntity[];
  isLoadingDocumentCategories: boolean;
  documentCategoriesError: CategoriesError | null;

  // Active Category (for editing)
  activeCategory: TemplateCategoryEntity | null;

  // Action States
  isPerformingAction: boolean;
  actionError: CategoriesError | null;

  // Actions - Load
  loadCategories: (type: TemplateCategoryType, includeInactive?: boolean) => Promise<void>;
  loadFormCategories: (includeInactive?: boolean) => Promise<void>;
  loadDocumentCategories: (includeInactive?: boolean) => Promise<void>;
  refreshAll: () => Promise<void>;

  // Actions - CRUD
  createCategory: (dto: CreateTemplateCategoryDto) => Promise<string | null>;
  updateCategory: (id: string, dto: UpdateTemplateCategoryDto) => Promise<boolean>;
  deleteCategory: (id: string) => Promise<boolean>;
  reorderCategories: (type: TemplateCategoryType, categoryIds: string[]) => Promise<boolean>;

  // Actions - Active Category
  setActiveCategory: (category: TemplateCategoryEntity | null) => void;
  clearActiveCategory: () => void;

  // Utility
  getCategoriesByType: (type: TemplateCategoryType) => TemplateCategoryEntity[];
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  // Form Categories
  formCategories: [] as TemplateCategoryEntity[],
  isLoadingFormCategories: false,
  formCategoriesError: null as CategoriesError | null,

  // Document Categories
  documentCategories: [] as TemplateCategoryEntity[],
  isLoadingDocumentCategories: false,
  documentCategoriesError: null as CategoriesError | null,

  // Active Category
  activeCategory: null as TemplateCategoryEntity | null,

  // Action States
  isPerformingAction: false,
  actionError: null as CategoriesError | null,
});

// ============================================
// API Endpoints
// ============================================

const CATEGORIES_API = {
  LIST: '/api/categories',
  CREATE: '/api/categories',
  UPDATE: (id: string) => `/api/categories/${id}`,
  DELETE: (id: string) => `/api/categories/${id}`,
  REORDER: '/api/categories/reorder',
};

// ============================================
// Store Implementation
// ============================================

export const useCategoriesStore = create<CategoriesState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // Load Actions
  // ============================================

  loadCategories: async (type: TemplateCategoryType, includeInactive = false) => {
    if (type === 'form') {
      await get().loadFormCategories(includeInactive);
    } else {
      await get().loadDocumentCategories(includeInactive);
    }
  },

  loadFormCategories: async (includeInactive = false) => {
    set({ isLoadingFormCategories: true, formCategoriesError: null });
    try {
      const api = getApiClient();
      const params = new URLSearchParams();
      params.set('type', 'form');
      if (includeInactive) params.set('includeInactive', 'true');

      // ApiClient already unwraps {success, data} responses
      const categories = await api.get<TemplateCategoryEntity[]>(
        `${CATEGORIES_API.LIST}?${params.toString()}`
      );

      set({
        formCategories: categories || [],
        isLoadingFormCategories: false,
      });
    } catch (error) {
      set({
        isLoadingFormCategories: false,
        formCategoriesError: {
          code: 'LOAD_CATEGORIES_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load form categories',
        },
      });
    }
  },

  loadDocumentCategories: async (includeInactive = false) => {
    set({ isLoadingDocumentCategories: true, documentCategoriesError: null });
    try {
      const api = getApiClient();
      const params = new URLSearchParams();
      params.set('type', 'document');
      if (includeInactive) params.set('includeInactive', 'true');

      // ApiClient already unwraps {success, data} responses
      const categories = await api.get<TemplateCategoryEntity[]>(
        `${CATEGORIES_API.LIST}?${params.toString()}`
      );

      set({
        documentCategories: categories || [],
        isLoadingDocumentCategories: false,
      });
    } catch (error) {
      set({
        isLoadingDocumentCategories: false,
        documentCategoriesError: {
          code: 'LOAD_CATEGORIES_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load document categories',
        },
      });
    }
  },

  refreshAll: async () => {
    await Promise.all([
      get().loadFormCategories(true),
      get().loadDocumentCategories(true),
    ]);
  },

  // ============================================
  // CRUD Actions
  // ============================================

  createCategory: async (dto: CreateTemplateCategoryDto): Promise<string | null> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      // ApiClient already unwraps {success, data} responses
      const newCategory = await api.post<TemplateCategoryEntity>(CATEGORIES_API.CREATE, dto);

      // Add to appropriate list
      if (dto.type === 'form') {
        set((state) => ({
          formCategories: [...state.formCategories, newCategory].sort((a, b) => a.sortOrder - b.sortOrder),
          isPerformingAction: false,
        }));
      } else {
        set((state) => ({
          documentCategories: [...state.documentCategories, newCategory].sort((a, b) => a.sortOrder - b.sortOrder),
          isPerformingAction: false,
        }));
      }

      return newCategory.id;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'CREATE_CATEGORY_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create category',
        },
      });
      return null;
    }
  },

  updateCategory: async (id: string, dto: UpdateTemplateCategoryDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      // ApiClient already unwraps {success, data} responses
      const updatedCategory = await api.put<TemplateCategoryEntity>(CATEGORIES_API.UPDATE(id), dto);

      // Update in both lists (we don't know which one it belongs to without checking)
      set((state) => ({
        formCategories: state.formCategories.map((c) =>
          c.id === id ? updatedCategory : c
        ),
        documentCategories: state.documentCategories.map((c) =>
          c.id === id ? updatedCategory : c
        ),
        activeCategory: state.activeCategory?.id === id ? updatedCategory : state.activeCategory,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'UPDATE_CATEGORY_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update category',
        },
      });
      return false;
    }
  },

  deleteCategory: async (id: string): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(CATEGORIES_API.DELETE(id));

      // Remove from both lists (soft delete just marks as inactive)
      set((state) => ({
        formCategories: state.formCategories.map((c) =>
          c.id === id ? { ...c, isActive: false } : c
        ),
        documentCategories: state.documentCategories.map((c) =>
          c.id === id ? { ...c, isActive: false } : c
        ),
        activeCategory: state.activeCategory?.id === id ? null : state.activeCategory,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'DELETE_CATEGORY_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete category',
        },
      });
      return false;
    }
  },

  reorderCategories: async (type: TemplateCategoryType, categoryIds: string[]): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      // ApiClient already unwraps {success, data} responses
      const categories = await api.put<TemplateCategoryEntity[]>(CATEGORIES_API.REORDER, { type, categoryIds });

      // Update the appropriate list with new order
      if (type === 'form') {
        set({
          formCategories: categories || [],
          isPerformingAction: false,
        });
      } else {
        set({
          documentCategories: categories || [],
          isPerformingAction: false,
        });
      }

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'REORDER_CATEGORIES_FAILED',
          message: error instanceof Error ? error.message : 'Failed to reorder categories',
        },
      });
      return false;
    }
  },

  // ============================================
  // Active Category Actions
  // ============================================

  setActiveCategory: (category: TemplateCategoryEntity | null) => {
    set({ activeCategory: category });
  },

  clearActiveCategory: () => {
    set({ activeCategory: null });
  },

  // ============================================
  // Utility Actions
  // ============================================

  getCategoriesByType: (type: TemplateCategoryType) => {
    const { formCategories, documentCategories } = get();
    return type === 'form' ? formCategories : documentCategories;
  },

  clearError: () => {
    set({
      formCategoriesError: null,
      documentCategoriesError: null,
      actionError: null,
    });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Get active form categories only */
export const useActiveFormCategories = () => {
  return useCategoriesStore((state) =>
    state.formCategories.filter((c) => c.isActive)
  );
};

/** Get active document categories only */
export const useActiveDocumentCategories = () => {
  return useCategoriesStore((state) =>
    state.documentCategories.filter((c) => c.isActive)
  );
};

/** Check if any loading state is active */
export const useCategoriesLoading = () => {
  const isLoadingForm = useCategoriesStore((state) => state.isLoadingFormCategories);
  const isLoadingDoc = useCategoriesStore((state) => state.isLoadingDocumentCategories);
  const isPerformingAction = useCategoriesStore((state) => state.isPerformingAction);
  return isLoadingForm || isLoadingDoc || isPerformingAction;
};

/** Get category by ID */
export const useCategoryById = (id: string) => {
  return useCategoriesStore((state) => {
    const formCat = state.formCategories.find((c) => c.id === id);
    if (formCat) return formCat;
    return state.documentCategories.find((c) => c.id === id);
  });
};

/** Get categories count by type */
export const useCategoriesCount = (type: TemplateCategoryType) => {
  return useCategoriesStore((state) =>
    type === 'form'
      ? state.formCategories.filter((c) => c.isActive).length
      : state.documentCategories.filter((c) => c.isActive).length
  );
};
